import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { pipe, switchMap, tap } from 'rxjs';
import { DateTime } from 'luxon';
import {
  setPropError,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import {
  DreReportNodeViewModel,
  DreReportPeriodViewModel,
  DreReportTotalsViewModel,
  DreReportViewModel,
} from './entities/dre-report.model';
import { DreReportService } from './infra/dre-report.service';
import { getDateTime } from '@shared/utils';

/**
 * `DreReportStore` — signal store que orquestra a busca e o estado da DRE
 * Gerencial. Os recursos avançados (loadMonthly, AV%, sparkline, drill-down)
 * são adicionados pelas user stories US3+, mantendo backwards-compat.
 *
 * Consultar [data-model.md](../../../../../specs/010-dre-gerencial/data-model.md)
 * para state machine e [research.md R1](../../../../../specs/010-dre-gerencial/research.md).
 */

/**
 * Seleção atual do filtro de período. Origem do auto-fetch via `setSelection`.
 *
 * `viewMode = 'single'` ⇒ uma única chamada `getSinglePeriod`.
 * `viewMode = 'monthly'` ⇒ (US3) chamadas paralelas `getSinglePeriod` + `getMonthlyPeriods`.
 */
export interface DreReportSelection {
  startDate: string;
  endDate: string;
  viewMode: 'single' | 'monthly';
}

/**
 * Visibilidade das linhas da tabela. `'all'` mostra tudo; `'non-zero'` filtra
 * folhas com `Amount = 0` e grupos cujos descendentes são todos zerados (US4).
 */
export type DreLineVisibility = 'all' | 'non-zero';

/**
 * Modo do gráfico do topo (US3). `'line'` é o default.
 */
export type DreChartMode = 'line' | 'bars';

/**
 * Modo de visualização da tabela: `'cumulative'` mostra apenas a coluna
 * acumulada do período (default); `'monthly'` adiciona uma coluna por mês
 * dentro do intervalo, lendo das séries de `currentMonthlyPeriods`.
 */
export type DreTableViewMode = 'cumulative' | 'monthly';

/**
 * Modo da coluna "YoY" da tabela: `'percent'` mostra variação percentual
 * (ex.: `+16,3%`); `'absolute'` mostra diferença absoluta em moeda
 * (ex.: `+R$ 3.927`). Estado puramente de UI — não afeta a 2ª chamada
 * (sempre a mesma seleção deslocada -1 ano).
 */
export type DreYoyMode = 'percent' | 'absolute';

/**
 * Linha achatada da árvore — combina campos do nó do backend com metadata
 * de hierarquia para alimentar a `lib-simple-grid-list`.
 *
 * `ParentChain` contém os NodeIds dos ancestrais (root → pai direto), usado
 * pelo componente de tabela para determinar visibilidade conforme
 * `expandedRowKeys`. Casing PascalCase consistente com `ChartOfAccountsRow`.
 *
 * `avPercent` é `(Amount / ReceitaLíquida) × 100` ou `null` quando
 * `ReceitaLíquida = 0` (US5 / FR-022). Sempre presente — a coluna é
 * exibida incondicionalmente pela tabela.
 */
export interface FlattenedDreRow {
  NodeId: string;
  Code: string;
  Name: string;
  Level: number;
  Amount: number;
  IsLeaf: boolean;
  Depth: number;
  HasChildren: boolean;
  ParentChain: string[];
  avPercent: number | null;
  /**
   * `true` para as linhas sintéticas de subtotal derivado (Receita Líquida,
   * Lucro Bruto, Resultado Operacional, Resultado Líquido) que o store
   * intercala entre as seções 1→2, 2→3, 3→4 e ao final. Diferenciador
   * que o componente da tabela usa para tipografia destacada, ocultar
   * chevron/drill-down e traduzir `Name` (que carrega a i18n key, não um
   * label literal).
   */
  isDerivedTotal: boolean;
  /**
   * Amount do **mesmo nó** no período anterior (range deslocado em -1 ano,
   * 2ª chamada paralela do mesmo endpoint). `null` enquanto a chamada de
   * prior não responde ou quando o nó não existe na resposta anterior
   * (ex.: conta criada neste ano). Coluna "ANO ANTERIOR" da tabela.
   */
  priorAmount: number | null;
  /**
   * Variação YoY em percentual: `((Amount − priorAmount) / |priorAmount|) × 100`.
   * `null` quando `priorAmount` é `null` ou `0` (evita `Infinity` / divisão
   * por zero). Coluna "YOY" da tabela.
   */
  yoyPercent: number | null;
}

interface DreReportStoreState {
  currentSelection: DreReportSelection | null;
  singleResult: DreReportViewModel | null;
  monthlyResult: DreReportPeriodViewModel[] | null;
  /**
   * Resposta do mesmo endpoint com o range deslocado em -1 ano (2ª chamada
   * paralela). Alimenta a coluna "ANO ANTERIOR" e a YoY da tabela. `null`
   * antes do primeiro fetch ou quando o backend devolveu erro só na chamada
   * prior — o `singleResult` corrente continua válido (loadPrior tem seu
   * próprio request-state).
   */
  priorResult: DreReportViewModel | null;
  /**
   * Série mensal do mesmo intervalo deslocado em -1 ano. Alimenta o
   * comparativo lado-a-lado no gráfico de barras (`Receita Líquida
   * 2026 × 2025`). `null` enquanto a chamada prior monthly não responde
   * ou quando `viewMode === 'single'`.
   */
  priorMonthlyResult: DreReportPeriodViewModel[] | null;
  lastFetchedAt: string | null;
  lineVisibility: DreLineVisibility;
  chartMode: DreChartMode;
  tableViewMode: DreTableViewMode;
  /**
   * Toggle "Mostrar AV%" da tabela (FR-020 / US5). Quando `false`, a coluna
   * de Análise Vertical fica oculta na UI e ausente nos exports. Default `false`
   * para preservar o layout compacto até o usuário pedir explicitamente.
   */
  avPercentEnabled: boolean;
  /**
   * Timestamp (epoch ms) da última "batida do relógio" enviada por
   * `tickClock()`. Alimenta o computed `isStale` reativamente — sem isso o
   * staleness só mudaria em re-renders por outras razões. `null` antes do
   * primeiro tick: nesse estado `isStale` é sempre `false`.
   */
  currentTickMs: number | null;
  /**
   * Modo da coluna "YoY" da tabela. Default `'percent'` para manter o
   * padrão de relatórios financeiros (variação percentual primeiro).
   */
  yoyMode: DreYoyMode;
}

const initialState: DreReportStoreState = {
  currentSelection: null,
  singleResult: null,
  monthlyResult: null,
  priorResult: null,
  priorMonthlyResult: null,
  lastFetchedAt: null,
  lineVisibility: 'all',
  chartMode: 'bars',
  tableViewMode: 'cumulative',
  avPercentEnabled: false,
  currentTickMs: null,
  yoyMode: 'percent',
};

/** Threshold de staleness em milissegundos (FR-006: 5 minutos). */
const STALE_THRESHOLD_MS = 300_000;

const ALL_OPS = [
  'loadSingle',
  'loadMonthly',
  'loadPrior',
  'loadPriorMonthly',
] as const;

/**
 * Desloca o intervalo da seleção atrás em N anos para a chamada YoY.
 * Usado pela 2ª chamada paralela ("ano anterior") da tabela. Preserva o
 * `viewMode` e tolera ISO inválido (passa-through). Datas continuam em
 * ISO completo — a redução para DateOnly fica no boundary HTTP do
 * `DreReportService`.
 */
const shiftSelectionYears = (
  selection: DreReportSelection,
  years: number,
): DreReportSelection => {
  const shift = (iso: string): string => {
    const dt = getDateTime(iso);
    if (!dt?.isValid) return iso;
    return dt.minus({ years }).toISO() ?? iso;
  };
  return {
    ...selection,
    startDate: shift(selection.startDate),
    endDate: shift(selection.endDate),
  };
};

/**
 * Mapa que descreve onde injetar as linhas derivadas no flatten da árvore.
 * Ordem reflete o "DRE clássico": Receita Líquida vem **depois** da seção
 * `Code='1'` (RECEITAS), Lucro Bruto **depois** de `'2'` (CUSTOS), e assim
 * por diante. O Code é a chave estável definida em `DRE_REPORT_API.md`
 * (Código fixo do plano de contas).
 *
 * `nodeI18nKey` é a string carregada em `FlattenedDreRow.Name` para essas
 * linhas — o componente da tabela traduz via pipe `translate`. Manter o
 * acoplamento store↔i18n nesse mapa contido é aceitável e evita duplicar
 * a mesma lista de 4 sintéticos no componente.
 */
const DERIVED_TOTAL_ROWS: ReadonlyArray<{
  sectionCode: string;
  nodeId: string;
  nameI18nKey: string;
  totalKey: keyof DreReportTotalsViewModel;
}> = [
  {
    sectionCode: '1',
    nodeId: 'derived:ReceitaLiquida',
    nameI18nKey: 'TREASURY.DRE_GERENCIAL.TABLE.DERIVED_TOTALS.RECEITA_LIQUIDA',
    totalKey: 'ReceitaLiquida',
  },
  {
    sectionCode: '2',
    nodeId: 'derived:LucroBruto',
    nameI18nKey: 'TREASURY.DRE_GERENCIAL.TABLE.DERIVED_TOTALS.LUCRO_BRUTO',
    totalKey: 'LucroBruto',
  },
  {
    sectionCode: '3',
    nodeId: 'derived:ResultadoOperacional',
    nameI18nKey: 'TREASURY.DRE_GERENCIAL.TABLE.DERIVED_TOTALS.RESULTADO_OPERACIONAL',
    totalKey: 'ResultadoOperacional',
  },
  {
    sectionCode: '4',
    nodeId: 'derived:ResultadoLiquido',
    nameI18nKey: 'TREASURY.DRE_GERENCIAL.TABLE.DERIVED_TOTALS.RESULTADO_LIQUIDO',
    totalKey: 'ResultadoLiquido',
  },
];

/**
 * Constrói o mapa `NodeId → Amount[]` para as sparklines da tabela (US6 / FR-024).
 *
 * Para cada nó (folha OU grupo) presente em qualquer um dos `periods`, gera
 * um array de tamanho `periods.length` com o `Amount` daquele nó em cada
 * período. Períodos onde o nó não aparece (ex.: conta criada no meio do
 * ano) ficam com `0` — preserva continuidade cronológica (FR-024).
 *
 * Aproveitamos que o backend já entrega `Amount` agregado em grupos
 * (`node.Amount = Σ Children.Amount`), então **não precisamos** somar
 * descendentes manualmente — basta visitar cada nó e ler `Amount`.
 */
export const buildSparklineMap = (
  periods: DreReportPeriodViewModel[],
): ReadonlyMap<string, number[]> => {
  const map = new Map<string, number[]>();
  const totalPeriods = periods.length;
  if (totalPeriods === 0) return map;

  const ensureSeries = (key: string): number[] => {
    let arr = map.get(key);
    if (!arr) {
      arr = new Array<number>(totalPeriods).fill(0);
      map.set(key, arr);
    }
    return arr;
  };

  const visit = (
    node: DreReportNodeViewModel,
    periodIndex: number,
  ): void => {
    ensureSeries(node.NodeId)[periodIndex] = node.Amount;
    for (const child of node.Children) {
      visit(child, periodIndex);
    }
  };

  periods.forEach((period, i) => {
    for (const root of period.Sections) {
      visit(root, i);
    }
    // Sintéticos: alimenta a sparkline/coluna mensal dos subtotais derivados
    // com o valor correspondente em DerivedTotals daquele mês. NodeId casa
    // com o injetado por flattenTree (mesma constante).
    for (const derived of DERIVED_TOTAL_ROWS) {
      ensureSeries(derived.nodeId)[i] = period.DerivedTotals[derived.totalKey];
    }
  });

  return map;
};

/**
 * Filtro recursivo "non-zero" (US4 / FR-016):
 * - Folha sobrevive se `Amount !== 0`.
 * - Grupo sobrevive se algum descendente sobrevive (após filtrar
 *   recursivamente seus próprios filhos).
 *
 * Retorna uma nova árvore preservando estrutura (não muta a original).
 */
export const filterTreeNonZero = (
  nodes: DreReportNodeViewModel[],
): DreReportNodeViewModel[] => {
  const out: DreReportNodeViewModel[] = [];
  for (const node of nodes) {
    if (node.IsLeaf) {
      if (node.Amount !== 0) out.push(node);
      continue;
    }
    const filteredChildren = filterTreeNonZero(node.Children);
    if (filteredChildren.length > 0) {
      out.push({ ...node, Children: filteredChildren });
    }
  }
  return out;
};

/**
 * Calcula `avPercent` para uma linha (US5 / FR-021):
 * `Amount / ReceitaLíquida × 100`. `null` quando ReceitaLíquida = 0
 * (consumidor renderiza `—` na coluna AV%).
 */
const computeAvPercent = (
  amount: number,
  receitaLiquida: number,
): number | null => {
  if (receitaLiquida === 0) return null;
  return (amount / receitaLiquida) * 100;
};

/**
 * Variação YoY em percentual: `((current − prior) / |prior|) × 100`.
 * Usa o valor absoluto no denominador para preservar o sinal de "melhora"
 * mesmo quando o prior é negativo (ex.: prejuízo virando lucro deve ser
 * uma variação positiva, não negativa).
 *
 * `null` quando:
 * - `prior === null` (resposta da 2ª chamada ainda não veio ou nó ausente).
 * - `prior === 0` (divisão por zero — consumidor exibe `—`).
 */
const computeYoyPercent = (
  current: number,
  prior: number | null,
): number | null => {
  if (prior === null || prior === 0) return null;
  return ((current - prior) / Math.abs(prior)) * 100;
};

/**
 * Constrói o índice `NodeId → Amount` do resultado anterior, percorrendo
 * a árvore `Sections` recursivamente e incluindo os sintéticos derivados
 * (chave `derived:*` apontando para o respectivo `DerivedTotals[key]`).
 * Pure function — testável sem store.
 */
const buildPriorAmountIndex = (
  prior: DreReportViewModel | null,
): ReadonlyMap<string, number> => {
  const map = new Map<string, number>();
  if (!prior) return map;
  const visit = (node: DreReportNodeViewModel): void => {
    map.set(node.NodeId, node.Amount);
    for (const child of node.Children) visit(child);
  };
  for (const root of prior.Sections) visit(root);
  // Inclui os sintéticos com a mesma convenção do flattenTree atual.
  for (const derived of DERIVED_TOTAL_ROWS) {
    map.set(derived.nodeId, prior.DerivedTotals[derived.totalKey]);
  }
  return map;
};

/**
 * Achata recursivamente a árvore `Sections` em um array linear preservando
 * `Depth` (profundidade 0-indexed para indentação visual), `ParentChain`
 * (cadeia de ancestrais usada para o filtro de expansão) e `HasChildren`
 * (necessário para o chevron). Cada linha recebe `avPercent` calculado
 * sobre a Receita Líquida do período (mesmo valor para todas as linhas).
 */
const flattenTree = (
  roots: DreReportNodeViewModel[],
  totals: DreReportTotalsViewModel,
  priorByNodeId: ReadonlyMap<string, number>,
): FlattenedDreRow[] => {
  const receitaLiquida = totals.ReceitaLiquida;
  const out: FlattenedDreRow[] = [];
  const priorOf = (nodeId: string): number | null =>
    priorByNodeId.has(nodeId) ? (priorByNodeId.get(nodeId) as number) : null;
  const visit = (
    node: DreReportNodeViewModel,
    depth: number,
    parentChain: string[],
  ): void => {
    const prior = priorOf(node.NodeId);
    out.push({
      NodeId: node.NodeId,
      Code: node.Code,
      Name: node.Name,
      Level: node.Level,
      Amount: node.Amount,
      IsLeaf: node.IsLeaf,
      Depth: depth,
      HasChildren: !node.IsLeaf && node.Children.length > 0,
      ParentChain: parentChain,
      avPercent: computeAvPercent(node.Amount, receitaLiquida),
      isDerivedTotal: false,
      priorAmount: prior,
      yoyPercent: computeYoyPercent(node.Amount, prior),
    });
    if (node.Children.length > 0) {
      const nextChain = [...parentChain, node.NodeId];
      for (const child of node.Children) {
        visit(child, depth + 1, nextChain);
      }
    }
  };
  for (const root of roots) {
    visit(root, 0, []);
    // Intercala subtotal derivado correspondente quando o Code da seção
    // bate com a tabela de mapeamento (1→Receita Líquida, 2→Lucro Bruto,
    // 3→Resultado Operacional, 4→Resultado Líquido do Exercício).
    const derived = DERIVED_TOTAL_ROWS.find((d) => d.sectionCode === root.Code);
    if (derived) {
      const amount = totals[derived.totalKey];
      const prior = priorOf(derived.nodeId);
      out.push({
        NodeId: derived.nodeId,
        Code: '=',
        Name: derived.nameI18nKey,
        Level: 1,
        Amount: amount,
        IsLeaf: true,
        Depth: 0,
        HasChildren: false,
        ParentChain: [],
        avPercent: computeAvPercent(amount, receitaLiquida),
        isDerivedTotal: true,
        priorAmount: prior,
        yoyPercent: computeYoyPercent(amount, prior),
      });
    }
  }
  return out;
};

/**
 * Converte um `DreReportViewModel` (single period) em um array de
 * `DreReportPeriodViewModel` com 1 elemento — usado pelo chart do topo
 * para uniformizar o consumo entre single e monthly mode (em single,
 * o gráfico de barras vira uma comparação simples de 2 colunas
 * atual × prior do mesmo mês). `Year`/`Month` extraídos do `StartDate`;
 * em data inválida ou input nulo, retorna `[]`.
 */
const singleToMonthlyArray = (
  result: DreReportViewModel | null,
): DreReportPeriodViewModel[] => {
  if (!result) return [];
  const start = getDateTime(result.StartDate);
  if (!start?.isValid) return [];
  return [
    {
      Year: start.year,
      Month: start.month,
      Label: start.toFormat('LLLL/yyyy'),
      StartDate: result.StartDate,
      EndDate: result.EndDate,
      DerivedTotals: result.DerivedTotals,
      Sections: result.Sections,
    },
  ];
};

/**
 * Margem Líquida = `ResultadoLiquido / ReceitaLiquida × 100`. Quando
 * `ReceitaLiquida = 0`, retorna `null` (consumidor exibe `—`, FR-008,
 * SC-005). Aceita totais nulos (estado pré-fetch).
 */
const computeMarginLiquida = (
  totals: DreReportTotalsViewModel | null,
): number | null => {
  if (!totals) return null;
  if (totals.ReceitaLiquida === 0) return null;
  return (totals.ResultadoLiquido / totals.ReceitaLiquida) * 100;
};

export const DreReportStore = signalStore(
  { providedIn: 'root' },
  withState<DreReportStoreState>(initialState),
  withReqState(...ALL_OPS),
  withComputed((store) => ({
    /**
     * Totais derivados do período corrente. `null` antes do primeiro fetch
     * bem-sucedido ou após erro.
     */
    currentTotals: computed<DreReportTotalsViewModel | null>(
      () => store.singleResult()?.DerivedTotals ?? null,
    ),
    /** Raízes da árvore — 4 ramos (RECEITAS / CUSTOS / etc.) ou `[]`. */
    currentTreeRoots: computed<DreReportNodeViewModel[]>(
      () => store.singleResult()?.Sections ?? [],
    ),
    /** Margem Líquida em % — `null` quando ReceitaLiquida = 0 ou sem dados. */
    marginLiquida: computed<number | null>(() =>
      computeMarginLiquida(store.singleResult()?.DerivedTotals ?? null),
    ),
    /**
     * Totais derivados do **mesmo período do ano anterior** (2ª chamada
     * paralela com `startDate`/`endDate` deslocados em -1 ano). `null`
     * enquanto a chamada não responde ou em erro.
     */
    priorTotals: computed<DreReportTotalsViewModel | null>(
      () => store.priorResult()?.DerivedTotals ?? null,
    ),
    /**
     * Índice `NodeId → Amount` do período anterior, incluindo as chaves
     * sintéticas `derived:*` apontando para os respectivos `DerivedTotals`.
     * Memoizado pelo signal de `priorResult` — recomputa só quando a 2ª
     * chamada devolve um novo payload.
     */
    priorAmountByNodeId: computed<ReadonlyMap<string, number>>(
      () => buildPriorAmountIndex(store.priorResult()),
    ),
    /**
     * Linhas achatadas para a tabela hierárquica. Aplica o filtro `lineVisibility`
     * recursivamente (US4), anexa `avPercent` (US5) e — quando a 2ª chamada
     * (prior) já respondeu — também `priorAmount` + `yoyPercent` por linha.
     * O sparkline por linha (US6) é fornecido por outro computed.
     */
    flattenedRows: computed<FlattenedDreRow[]>(() => {
      const result = store.singleResult();
      if (!result) return [];
      const visibility = store.lineVisibility();
      const baseSections =
        visibility === 'non-zero'
          ? filterTreeNonZero(result.Sections)
          : result.Sections;
      const priorIndex = buildPriorAmountIndex(store.priorResult());
      return flattenTree(baseSections, result.DerivedTotals, priorIndex);
    }),
    /**
     * Períodos mensais (um por mês calendário cheio do intervalo) — alimenta
     * gráficos do topo (US3) e sparklines por linha (US6). Vazio até a primeira
     * chamada com `viewMode = 'monthly'` ou após `setSelection` com viewMode 'single'.
     */
    currentMonthlyPeriods: computed<DreReportPeriodViewModel[]>(
      () => store.monthlyResult() ?? [],
    ),
    /**
     * Períodos mensais do **mesmo intervalo deslocado em -1 ano** — alimenta
     * o gráfico comparativo (barras `2026 × 2025`). Vazio quando
     * `viewMode === 'single'`. A ordem casa 1:1 com `currentMonthlyPeriods`
     * (mesmo número de elementos), permitindo merge por índice no consumidor.
     */
    currentPriorMonthlyPeriods: computed<DreReportPeriodViewModel[]>(
      () => store.priorMonthlyResult() ?? [],
    ),
    /**
     * Períodos do **gráfico do topo** — uniformiza single + monthly:
     *  - `viewMode === 'monthly'` → retorna `currentMonthlyPeriods` direto.
     *  - `viewMode === 'single'` → sintetiza array de 1 elemento a partir
     *    do `singleResult` (extrai Year/Month do `StartDate`), permitindo
     *    o bar chart renderizar 2 barras de comparação (atual × prior)
     *    mesmo quando o usuário escolheu "Mês corrente"/"Mês anterior".
     *
     * Vazio quando nenhum dos dois resultados ainda foi carregado.
     */
    chartCurrentPeriods: computed<DreReportPeriodViewModel[]>(() => {
      const monthly = store.monthlyResult();
      if (monthly && monthly.length > 0) return monthly;
      return singleToMonthlyArray(store.singleResult());
    }),
    /**
     * Análogo a `chartCurrentPeriods` para o range prior (1 ano atrás).
     * Casa 1:1 por índice — o consumidor (chart) intercala prior/current
     * sem precisar saber se está em single ou monthly mode.
     */
    chartPriorPeriods: computed<DreReportPeriodViewModel[]>(() => {
      const monthly = store.priorMonthlyResult();
      if (monthly && monthly.length > 0) return monthly;
      return singleToMonthlyArray(store.priorResult());
    }),
    /**
     * Mapa `NodeId → number[]` com a série mensal de cada nó da
     * árvore (folhas e grupos), pronto para alimentar `lib-line-chart`
     * na coluna "Tendência" da tabela. Vazio quando não há série mensal
     * (viewMode = 'single' ou ainda não buscado) — o consumidor da tabela
     * exibe estado contextual conforme `intervalSpansMultipleMonths`.
     */
    sparklineValuesByNodeId: computed<ReadonlyMap<string, number[]>>(
      () => buildSparklineMap(store.monthlyResult() ?? []),
    ),
    /**
     * `true` quando a seleção atual abrange ≥ 2 meses calendário (`viewMode = 'monthly'`).
     * Controla visibilidade do gráfico do topo: quando false, o componente
     * exibe estado contextual "Selecione mais de um mês para ver tendência"
     * em vez de renderizar gráfico com 1 ponto (FR-034, US3 AS5).
     */
    intervalSpansMultipleMonths: computed<boolean>(
      () => store.currentSelection()?.viewMode === 'monthly',
    ),
    /**
     * Banner "Atualizar" (FR-006): `true` quando o último fetch é anterior
     * ao threshold de 5 minutos em relação ao `currentTickMs`. Por design o
     * cálculo só dispara depois do primeiro `tickClock()` — antes disso
     * `currentTickMs` é `null` e o resultado é `false`, evitando o flash
     * de "desatualizado" logo após o primeiro carregamento. O page-feature
     * agenda o tick (`interval(60_000)`) para reavaliar a cada minuto sem
     * refazer fetch.
     */
    isStale: computed<boolean>(() => {
      const tick = store.currentTickMs();
      const fetched = store.lastFetchedAt();
      if (tick === null || fetched === null) return false;
      const fetchedMs = getDateTime(fetched)?.toMillis() ?? Number.NaN;
      if (Number.isNaN(fetchedMs)) return false;
      return tick - fetchedMs > STALE_THRESHOLD_MS;
    }),
  })),
  withMethods((store, service = inject(DreReportService)) => {
    /**
     * rxMethod que busca a DRE single-period. US3 estenderá com `loadMonthly`
     * paralelo via `forkJoin` quando `viewMode === 'monthly'`.
     */
    const loadSingle = rxMethod<DreReportSelection>(
      pipe(
        tap(() => patchState(store, setPropLoading('loadSingle'))),
        switchMap((selection) =>
          service
            .getSinglePeriod({
              startDate: selection.startDate,
              endDate: selection.endDate,
            })
            .pipe(
              tapResponse({
                next: (singleResult) => {
                  patchState(
                    store,
                    {
                      singleResult,
                      lastFetchedAt: DateTime.now().toISO(),
                    },
                    setPropLoaded('loadSingle'),
                  );
                },
                error: (error: unknown) => {
                  patchState(store, setPropError('loadSingle', error));
                },
              }),
            ),
        ),
      ),
    );

    /**
     * rxMethod paralelo que busca a série mensal (`groupByMonth=true`). Alimenta
     * `monthlyResult` que vira `currentMonthlyPeriods`. Só é disparada quando
     * `viewMode === 'monthly'` — para `single`, mantém o valor anterior em cache.
     *
     * Decisão arquitetural: 2 chamadas paralelas (loadSingle + loadMonthly)
     * em vez de 1 chamada agregada no front. Ver [contracts/dre-report.api.md](../../../../../../specs/010-dre-gerencial/contracts/dre-report.api.md).
     */
    const loadMonthly = rxMethod<DreReportSelection>(
      pipe(
        tap(() => patchState(store, setPropLoading('loadMonthly'))),
        switchMap((selection) =>
          service
            .getMonthlyPeriods({
              startDate: selection.startDate,
              endDate: selection.endDate,
            })
            .pipe(
              tapResponse({
                next: (monthlyResult) => {
                  patchState(
                    store,
                    { monthlyResult },
                    setPropLoaded('loadMonthly'),
                  );
                },
                error: (error: unknown) => {
                  patchState(store, setPropError('loadMonthly', error));
                },
              }),
            ),
        ),
      ),
    );

    /**
     * rxMethod paralelo que busca a **série mensal** do mesmo intervalo
     * deslocado em -1 ano. Alimenta `priorMonthlyResult` → comparativo
     * lado-a-lado no gráfico de barras. Só faz sentido quando o intervalo
     * abrange ≥ 2 meses (`viewMode === 'monthly'`) — o `setSelection`
     * só dispara nesse caso.
     */
    const loadPriorMonthly = rxMethod<DreReportSelection>(
      pipe(
        tap(() => patchState(store, setPropLoading('loadPriorMonthly'))),
        switchMap((selection) =>
          service
            .getMonthlyPeriods({
              startDate: selection.startDate,
              endDate: selection.endDate,
            })
            .pipe(
              tapResponse({
                next: (priorMonthlyResult) => {
                  patchState(
                    store,
                    { priorMonthlyResult },
                    setPropLoaded('loadPriorMonthly'),
                  );
                },
                error: (error: unknown) => {
                  patchState(store, setPropError('loadPriorMonthly', error));
                },
              }),
            ),
        ),
      ),
    );

    /**
     * rxMethod paralelo que busca o mesmo período do ano anterior (range
     * deslocado em -1 ano via `shiftSelectionYears`). Alimenta `priorResult`
     * → `priorTotals` + colunas "ANO ANTERIOR" / "YOY" da tabela. Estado de
     * requisição independente: erro aqui não afeta `singleResult`/`monthlyResult`
     * (a tela degrada apenas as colunas YoY).
     */
    const loadPrior = rxMethod<DreReportSelection>(
      pipe(
        tap(() => patchState(store, setPropLoading('loadPrior'))),
        switchMap((selection) =>
          service
            .getSinglePeriod({
              startDate: selection.startDate,
              endDate: selection.endDate,
            })
            .pipe(
              tapResponse({
                next: (priorResult) => {
                  patchState(
                    store,
                    { priorResult },
                    setPropLoaded('loadPrior'),
                  );
                },
                error: (error: unknown) => {
                  patchState(store, setPropError('loadPrior', error));
                },
              }),
            ),
        ),
      ),
    );

    /**
     * Atualiza a seleção atual e dispara as buscas correspondentes:
     * - `viewMode === 'single'` → `loadSingle` + `loadPrior` (YoY do mesmo mês).
     *   `monthlyResult` é resetado para `null` (gráfico do topo exibirá estado contextual).
     * - `viewMode === 'monthly'` → `loadSingle` + `loadMonthly` + `loadPrior`,
     *   tudo paralelo (cada um tem seu próprio request-state).
     *
     * Re-emissão de uma seleção idêntica também dispara (semântica de refresh).
     */
    const setSelection = (selection: DreReportSelection): void => {
      patchState(store, { currentSelection: selection });
      loadSingle(selection);
      loadPrior(shiftSelectionYears(selection, 1));
      if (selection.viewMode === 'monthly') {
        loadMonthly(selection);
        loadPriorMonthly(shiftSelectionYears(selection, 1));
      } else {
        // Limpa séries mensais stale do período anterior — evita que um
        // gráfico antigo permaneça visível enquanto a nova consulta single
        // carrega. Aplica tanto na série corrente quanto na de comparação.
        patchState(store, {
          monthlyResult: null,
          priorMonthlyResult: null,
        });
      }
    };

    /** Re-emite a seleção atual para refazer todas as buscas conforme viewMode. */
    const refresh = (): void => {
      const current = store.currentSelection();
      if (!current) return;
      loadSingle(current);
      loadPrior(shiftSelectionYears(current, 1));
      if (current.viewMode === 'monthly') {
        loadMonthly(current);
        loadPriorMonthly(shiftSelectionYears(current, 1));
      }
    };

    /** Define o modo do gráfico do topo. Não dispara fetch (puro estado de UI). */
    const setChartMode = (mode: DreChartMode): void => {
      patchState(store, { chartMode: mode });
    };

    /**
     * Define a visibilidade das linhas da tabela (US4). `'all'` mostra tudo;
     * `'non-zero'` filtra folhas com `Amount = 0` e grupos cujos descendentes
     * são todos zerados. Não dispara fetch — apenas re-deriva `flattenedRows`.
     */
    const setLineVisibility = (visibility: DreLineVisibility): void => {
      patchState(store, { lineVisibility: visibility });
    };

    /**
     * Alterna entre coluna acumulada única e colunas mensais na tabela.
     * Não dispara fetch — o array `monthlyResult` já está em memória sempre
     * que `viewMode = 'monthly'` foi selecionado no filtro de período.
     */
    const setTableViewMode = (mode: DreTableViewMode): void => {
      patchState(store, { tableViewMode: mode });
    };

    /**
     * Liga/desliga a coluna AV% (FR-020 / US5). Apenas estado de UI; não
     * dispara fetch nem altera `flattenedRows`.
     */
    const setAvPercentEnabled = (enabled: boolean): void => {
      patchState(store, { avPercentEnabled: enabled });
    };

    /**
     * Alterna o modo da coluna "YoY" entre variação percentual e variação
     * absoluta em moeda. Não dispara fetch — os dados de prior já estão em
     * memória; o componente da tabela reformata com `Amount − priorAmount`.
     */
    const setYoyMode = (mode: DreYoyMode): void => {
      patchState(store, { yoyMode: mode });
    };

    /**
     * Atualiza o "tick" usado pelo computed `isStale`. Quando chamado sem
     * argumento, usa `Date.now()`. O parâmetro explícito existe para testes
     * determinísticos (e para o page-feature opcionalmente injetar um clock
     * controlado em e2e). O page chama isto a cada minuto via `interval(60_000)`
     * para reavaliar o banner "Atualizar" sem refetch.
     */
    const tickClock = (timestamp?: number): void => {
      patchState(store, { currentTickMs: timestamp ?? DateTime.now().toMillis() });
    };

    return {
      setSelection,
      refresh,
      setChartMode,
      setLineVisibility,
      setTableViewMode,
      setAvPercentEnabled,
      setYoyMode,
      tickClock,
    };
  }),
);
